import { useState, useEffect } from 'react';

const ProtectedCultivation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStructure, setActiveStructure] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('protected-cultivation');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const structures = [
    {
      title: 'Greenhouse',
      subtitle: 'Glass Structure Excellence',
      description: 'Traditional glass greenhouse with superior light transmission and durability. Perfect for premium crop production with precise climate control.',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'ri-home-4-line',
      features: ['Glass Construction', 'Maximum Light', 'Precise Control', 'Long Lasting'],
      benefits: ['95% Light Transmission', 'Temperature Control ±2°C', '20+ Year Lifespan', 'Premium Crop Quality'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Polyhouse',
      subtitle: 'Plastic Film Protection',
      description: 'Cost-effective polyethylene film structure offering excellent protection from weather while maintaining optimal growing conditions for various crops.',
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'ri-building-2-line',
      features: ['PE Film Cover', 'Cost Effective', 'Easy Installation', 'Flexible Design'],
      benefits: ['80% Cost Savings', 'Quick Setup', 'Weather Protection', 'Good Ventilation'],
      color: 'from-green-500 to-emerald-500'
    },
    // {
    //   title: 'Glasshouse',
    //   subtitle: 'Premium Glass Cultivation',
    //   description: 'High-end glass structure with advanced automation, climate control, and monitoring systems for commercial-grade crop production.',
    //   image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    //   icon: 'ri-home-5-line',
    //   features: ['Tempered Glass', 'Automation Ready', 'Advanced Systems', 'Commercial Grade'],
    //   benefits: ['Automated Control', 'Maximum Yield', 'Professional Grade', 'Smart Monitoring'],
    //   color: 'from-purple-500 to-indigo-500'
    // }
  ];

  const advantages = [
    {
      icon: 'ri-shield-check-line',
      title: 'Weather Protection',
      description: 'Shield crops from harsh weather, pests, and diseases'
    },
    {
      icon: 'ri-temp-hot-line',
      title: 'Climate Control',
      description: 'Maintain optimal temperature and humidity levels'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Year-Round Growing',
      description: 'Extend growing seasons and increase annual harvests'
    },
    {
      icon: 'ri-water-percent-line',
      title: 'Water Efficiency',
      description: 'Reduce water usage through controlled irrigation'
    },
    {
      icon: 'ri-bug-line',
      title: 'Pest Management',
      description: 'Better control over pests and disease prevention'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Higher Yields',
      description: 'Increase productivity by 3-5x compared to open field'
    }
  ];

  return (
    <section id="protected-cultivation" className="py-20 bg-gradient-to-b from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Protected Cultivation Systems
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Advanced greenhouse and polyhouse technologies for optimal crop production in controlled environments
          </p>
        </div>

        {/* Advantages Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {advantages.map((advantage, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center"
            >
              <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-slate-100 to-gray-100 rounded-full flex items-center justify-center text-slate-600">
                <i className={`${advantage.icon} text-xl`}></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">{advantage.title}</h3>
              <p className="text-xs text-gray-600">{advantage.description}</p>
            </div>
          ))}
        </div>

        {/* Structure Selection */}
        <div className={`mb-8 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="flex flex-wrap justify-center gap-4">
            {structures.map((structure, index) => (
              <button
                key={index}
                onClick={() => setActiveStructure(index)}
                className={`px-8 py-4 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeStructure === index
                    ? 'bg-slate-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-slate-50 hover:text-slate-600'
                }`}
              >
                {structure.title}
              </button>
            ))}
          </div>
        </div>

        {/* Active Structure Display */}
        <div className={`bg-white rounded-3xl overflow-hidden shadow-2xl mb-16 transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="lg:flex">
            <div className="lg:w-1/2">
              <img 
                src={structures[activeStructure].image}
                alt={structures[activeStructure].title}
                className="w-full h-64 lg:h-full object-cover"
              />
            </div>
            <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
              <div className={`w-16 h-16 mb-6 rounded-full bg-gradient-to-br ${structures[activeStructure].color} flex items-center justify-center text-white`}>
                <i className={`${structures[activeStructure].icon} text-2xl`}></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">
                {structures[activeStructure].title}
              </h3>
              <h4 className="text-xl text-slate-600 font-semibold mb-4">
                {structures[activeStructure].subtitle}
              </h4>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {structures[activeStructure].description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3">Key Features</h5>
                  <div className="space-y-2">
                    {structures[activeStructure].features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-slate-600 text-xs"></i>
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-800 mb-3">Benefits</h5>
                  <div className="space-y-2">
                    {structures[activeStructure].benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                          <i className="ri-star-line text-green-600 text-xs"></i>
                        </div>
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Chart */}
        <div className={`bg-gradient-to-r from-slate-600 to-gray-600 rounded-3xl p-8 md:p-12 text-white transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-8">
            Choose Your Protection Level
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {structures.map((structure, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${structure.color} flex items-center justify-center`}>
                  <i className={`${structure.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-xl font-bold mb-2">{structure.title}</h4>
                <p className="text-slate-200 text-sm mb-4">{structure.subtitle}</p>
                <div className="space-y-2">
                  {structure.features.slice(0, 2).map((feature, idx) => (
                    <div key={idx} className="text-sm text-slate-300">• {feature}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-xl text-slate-200 mb-6">
              From cost-effective polyhouses, find the perfect solution for your farming needs
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProtectedCultivation;